import type { Delegate } from "@p0security/shared/types/access/index";

/** The two `aws` accesses a satellite can attach a delegate to. */
export type AwsDelegatingUse = "policy" | "resource";

/**
 * `aws`'s "policy" and "resource" accesses each delegate to `aws-midc` and
 * `aws-oidc`, but `aws/accesses.ts` can't import either satellite's
 * `accesses.ts` without recreating an `aws` <-> `aws-midc` / `aws-oidc`
 * cycle. Instead, each satellite's `registry.ts` calls
 * {@link registerAwsDelegate} at module init to populate the records below,
 * which `aws/accesses.ts` reads lazily through its `delegation` field.
 * Composition roots guarantee both satellites have registered by import
 * time by importing `@p0security/integrations-registry` first.
 */
const delegates: Record<
  AwsDelegatingUse,
  Record<string, Delegate<any, any>>
> = {
  policy: {},
  resource: {},
};

/** Throws on a read of an empty delegates record, rather than silently
 * returning `undefined`, if neither satellite has registered yet.
 */
const sealAwsDelegates = (
  use: AwsDelegatingUse,
  target: Record<string, Delegate<any, any>>
): Record<string, Delegate<any, any>> => {
  if (
    typeof process !== "undefined" &&
    process.env?.NODE_ENV === "production"
  ) {
    return target;
  }

  const fail = (operation: string): never => {
    throw new Error(
      `aws's "${use}" delegates record was read (${operation}) before either aws-midc or aws-oidc registered. Composition roots must import @p0security/integrations-registry (or otherwise import both satellites' registry.ts) before reading it.`
    );
  };

  return new Proxy(target, {
    get(proxied, property, receiver) {
      if (typeof property === "string" && Object.keys(proxied).length === 0) {
        fail(`read of '${property}'`);
      }
      return Reflect.get(proxied, property, receiver);
    },
    ownKeys(proxied) {
      if (Object.keys(proxied).length === 0) fail("enumeration");
      return Reflect.ownKeys(proxied);
    },
  });
};

/** `aws/accesses.ts`'s "policy" and "resource" accesses each set their
 * `delegation` field to one of these -- a live reference, not a snapshot, so
 * a satellite that registers after `aws/accesses.ts` has already evaluated
 * still shows up. See this module's doc comment above.
 */
export const awsDelegates: Record<
  AwsDelegatingUse,
  Record<string, Delegate<any, any>>
> = {
  policy: sealAwsDelegates("policy", delegates.policy),
  resource: sealAwsDelegates("resource", delegates.resource),
};

/** Called once per satellite, at module scope in its own `registry.ts`
 * (`aws-midc/registry.ts`, `aws-oidc/registry.ts`), to add its accesses to
 * one of `aws`'s delegates records.
 */
export const registerAwsDelegate = (
  use: AwsDelegatingUse,
  key: string,
  delegate: Delegate<any, any>
): void => {
  delegates[use][key] = delegate;
};
